// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import { SubmissionError } from 'redux-form'
import { UNCAUGHT_ERROR } from 'errors'
import { loadingToggler } from 'redux/modules/loading'
import makePathDbFormQuery from 'components/SearchAndSelect/helpers/makePathDbFormQuery'
import { makeProjectManagementFormQuery } from 'components/SearchAndSelect/Sets/ProjectManagement/helpers'
import { documentGenerationNotification } from 'components/Notifications'
import { castToBool } from 'helpers/reduxFormFunctions'

import {
  assembleQuery,
  postPathReportExport,
  postProjectPathReportExport,
} from 'helpers/api'

// NOTE - this function is called when submitting a document generation request from
// three difference workflows:
// - Project Summary
// - Path DB
// - Project Management

const exportPathData = reportConfig => (dispatch, getState) => (
  dispatch(loadingToggler(
    async () => {
      const {
        allRowsHighlighted,
        dataSource,
        highlightedRows,
        formData,
        sourceForm,
        datasheetVersion,
      } = reportConfig

      var {asc} = reportConfig

      const { pathDetails, projectSummary } = getState()
      const visiblePathIds = projectSummary.get('visiblePathIds')
      const pathId = pathDetails.get('pathId')

      const fileName = formData.fileName.trim().replace(/\s/g, '_')
      const pathExportTypes = formData.pathExportTypes.map(v => v.toUpperCase())

      const underline = !!formData.underline.length
      asc = castToBool(asc)

      const query = [{
        field: 'fileName',
        value: formData.fileName.trim().replace(/\s/g, '_'),
      }, {
        field: 'pathExportTypes',
        value: formData.pathExportTypes.join(),
      }, {
        field: 'underline',
        value: !!formData.underline.length,
      }, {
        field: 'asc',
        value: asc,
      }]


      const body = {
        fileName,
        pathExportTypes,
        underline,
        asc,
      }
      // we wont have a datasheetVersion defined when generating reports from the Path Database search.
      // but we will get either 'prePCN' or 'PCN' as datasheetVersion when generating reports from
      // the project management screen and the project summary screen.
      // if datasheetVersion === undefined then it will default to 'database' in the backend handling.
      if (datasheetVersion !== undefined) {
        query.push({
          field: 'datasheetVersion',
          value: datasheetVersion,
        })

        body.datasheetVersion = datasheetVersion.toUpperCase()
      }

      // Note - Adding the URL check here to make sure we are in path details or path and not in project summary.
      if (window.location.href.match(/project-management\/\d+\/path-details\/\d+$/) || window.location.href.match(/path\/\d+$/)) {
        query.push({
          field: dataSource,
          value: pathId,
        })

        body.pathIds = [pathId]
      } else if (highlightedRows.size === 0 || highlightedRows.length === 0) {
        // NOTE - this is intended specifically for when a user
        // is generating a path report form the Project Summary screen
        // however I added check for size AND length to account for the other scenarios
        // where this function is used.
        query.push({
          field: dataSource,
          // must join the array because of how API is expecting the value
          value: visiblePathIds.join(),
        })

        body.pathIds = Array.from(visiblePathIds.join().split(","))
      } else if (!allRowsHighlighted) {
        // export only the individual path ids or project ids which are highlighted in the UI.
        // NOTE - dataSource will be either 'paths' or 'projects'

        // NOTE - the Project summary can generate reports for its paths. For these scenarios,
        // allRowsHighlighted will always be false. This property is for
        // <SearchAndSelect /> to distinguish between when it should use all highlighted paths in the UI
        // vs all possible paths which could match a query.
        // Because of pagination, a query can return more entities than are in browser state.
        // This will never happen in the Project Summary though because we always have all of a project's paths in redux.

        query.push({
          field: dataSource,
          // must join the array because of how API is expecting the value
          value: highlightedRows.join(),
        })

        body.pathIds = Array.from(highlightedRows.join().split(","))
        //body.pathIds = [highlightedRows.join()]

      } else if (dataSource === 'paths') {
        // NOTE - because the path db search form has a lot of complex logic going on, we
        // need to distinguish if it's using the path search
        const { values } = getState().form[sourceForm]
        query.push(makePathDbFormQuery(values))

        body.push(makePathDbFormQuery(values))
      } else if (dataSource === 'projects') {
        // or if it's using the project management search.
        const { values } = getState().form[sourceForm]
        query.push(makeProjectManagementFormQuery(values))

        body.push(makeProjectManagementFormQuery(values))
      }

      const assembledQuery = assembleQuery(query)

      try {
        const { entity } = await (
          dataSource === 'paths'
            ? postPathReportExport(body)
            : postProjectPathReportExport(assembledQuery)
        )
        await dispatch(documentGenerationNotification(entity))
      } catch (err) {
        console.warn(err)
        const _error = err.message || UNCAUGHT_ERROR
        throw new SubmissionError({ _error })
      }
    })
  )
)

export default exportPathData
